import React, { useEffect, useState } from 'react';
import { BanknotesIcon, CalendarIcon, ChevronLeftIcon, CreditCardIcon } from '@heroicons/react/16/solid';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppUser } from '../../../../models/AppUser';
import { useAppUser } from '../../../../hooks/useAppUser';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { AppUserService } from '../../../../services/appUserService';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import Loading from '../../../../components/Loading';
import { Avatar } from '../../../../components/Avatar';
import { Heading, Subheading } from '../../../../components/Heading';
import { Badge } from '../../../../components/Badge';
import dateFormat from 'dateformat';
import { Button } from '../../../../components/Button';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../../../../components/Description-List';
import { Card } from '../../../../components/Card';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../../components/Dialog';
import { Input } from '../../../../components/Input';
import { Text } from '../../../../components/Text';

const ViewAppUserIdPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [user, setUser] = useState<AppUser | null>(null);
    const [isOpen, setIsOpen] = useState(false); // State for dialog open status
    const [isResetting, setIsResetting] = useState(false); // State for set-password loading
    const [resetMode, setResetMode] = useState<'set' | 'generate'>('set');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [sendEmailOnSet, setSendEmailOnSet] = useState(false);
    const navigate = useNavigate();
    const { fetchUserById, loading, error, } = useAppUser();

    useEffect(() => {
        document.title = "View App User";
    }, []);

    useEffect(() => {
        if (!id) return;

        const fetchUser = async () => {
            try {
                const userData = await fetchUserById(id);
                setUser(userData!);
            } catch (err) {
                console.error('Failed to fetch user details:', err);
            }
        };

        fetchUser();
    }, [id]);

    const closeResetDialog = () => {
        setIsOpen(false);
        setResetMode('set');
        setNewPassword('');
        setConfirmNewPassword('');
        setSendEmailOnSet(false);
    };

    // Function to handle admin directly setting a new password for the user
    const handleSetPassword = async () => {
        if (!user) return;
        if (newPassword.length < 6) {
            showErrorToast('New password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            showErrorToast('Passwords do not match');
            return;
        }
        setIsResetting(true);

        try {
            const result = await AppUserService.setPasswordDirect(user.id, newPassword, sendEmailOnSet);
            if (result.dbsuccess) {
                showSuccessToast(result.message);
                closeResetDialog();
            } else {
                showErrorToast(result.message);
            }
        } catch (err: any) {
            console.error('Failed to set password:', err);
            showErrorToast(err?.response?.data?.message || 'Failed to set password. Please try again.');
        } finally {
            setIsResetting(false);
        }
    };

    // Function to handle generating a random password and emailing it via Resend
    const handleGenerateAndEmail = async () => {
        if (!user) return;
        if (!user.email) {
            showErrorToast('This user has no email on file');
            return;
        }
        setIsResetting(true);

        try {
            const result = await AppUserService.resetPasswordByRandom(user.username);
            if (result.dbsuccess) {
                showSuccessToast(result.message);
                closeResetDialog();
            } else {
                showErrorToast(result.message);
            }
        } catch (err: any) {
            console.error('Failed to generate and email password:', err);
        } finally {
            setIsResetting(false);
        }
    };

    if (loading) return <Loading />;
    if (error) return <p className="py-16 text-center text-red-700">{error}</p>;
    if (!user) return <p className="py-16 text-center">User not found.</p>;

    return (
        <>
            <div className="max-lg:hidden">
                <Link to="/gp/app/users" className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    App Users
                </Link>
            </div>
            <div className="mt-4 lg:mt-8">
                <div className="flex items-center gap-4">
                    <Avatar square initials={user.username[0]} className="bg-zinc-300 text-gray-700 size-8" />
                    <Heading>{user.username}</Heading>
                </div>
                <div className="isolate mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-4">
                    <div className="flex flex-wrap gap-x-10 gap-y-4 py-1.5">
                        <span className="flex items-center gap-2 text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
                            <CalendarIcon className="size-4 shrink-0 fill-zinc-400 dark:fill-zinc-500" />
                            <span>{dateFormat(user.created_time, "mmmm dd, yyyy")}</span>
                        </span>
                    </div>
                    <div className="flex gap-4">
                        <Button outline onClick={() => setIsOpen(true)}>Set Password</Button>
                        <Button onClick={() => navigate(`/gp/app/users/edit/${user.id}`)}>Edit</Button>
                    </div>
                </div>
            </div>

            <Card className="mt-12 p-6">
                <Subheading>App User Information</Subheading>
                <DescriptionList className="mt-4">

                    <DescriptionTerm>Email</DescriptionTerm>
                    <DescriptionDetails>{user.email || '-'}</DescriptionDetails>
                    <DescriptionTerm>Phone</DescriptionTerm>
                    <DescriptionDetails>{user.mobile_no || '-'}</DescriptionDetails>

                </DescriptionList>
            </Card>
            <Card className="mt-12 p-6">
                <Subheading>Mobile App Account Information</Subheading>
                <DescriptionList className="mt-4">
                    <DescriptionTerm>Username</DescriptionTerm>
                    <DescriptionDetails>{user.username}</DescriptionDetails>
                    <DescriptionTerm>Warehouse</DescriptionTerm>
                    <DescriptionDetails className="font-medium">{user.warehouse_name || '-'}</DescriptionDetails>
                    <DescriptionTerm>Role</DescriptionTerm>
                    <DescriptionDetails className="font-medium">
                        {user.is_admin == '1' ? 'Admin' : user.is_admin == '2' ? 'Warehouse' : 'Salesman'}
                    </DescriptionDetails>
                </DescriptionList>
            </Card>

            {/* Set New Password Dialog */}
            <Dialog open={isOpen} onClose={closeResetDialog}>
                <DialogTitle>Set New Password</DialogTitle>
                <DialogDescription>
                    {resetMode === 'set'
                        ? 'Directly set a new password for this user. They can log in with it immediately.'
                        : 'Generate a random password and email it to the user via Resend.'}
                </DialogDescription>

                <div className="mt-4 flex gap-2">
                    {resetMode === 'set' ? (
                        <Button onClick={() => setResetMode('set')} disabled={isResetting}>Type a password</Button>
                    ) : (
                        <Button outline onClick={() => setResetMode('set')} disabled={isResetting}>Type a password</Button>
                    )}
                    {resetMode === 'generate' ? (
                        <Button onClick={() => setResetMode('generate')} disabled={isResetting}>Generate &amp; email</Button>
                    ) : (
                        <Button outline onClick={() => setResetMode('generate')} disabled={isResetting}>Generate &amp; email</Button>
                    )}
                </div>

                {resetMode === 'set' ? (
                    <div className="mt-4 space-y-4">
                        <div>
                            <Subheading>New Password</Subheading>
                            <div className="mt-2">
                                <Input
                                    type="password"
                                    aria-label="New Password"
                                    autoFocus
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                            </div>
                        </div>
                        <div>
                            <Subheading>Confirm New Password</Subheading>
                            <div className="mt-2">
                                <Input
                                    type="password"
                                    aria-label="Confirm New Password"
                                    value={confirmNewPassword}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSetPassword()}
                                />
                            </div>
                        </div>
                        <label className={`flex items-center gap-2 text-sm ${user.email ? 'text-zinc-700 dark:text-zinc-300' : 'text-zinc-400'}`}>
                            <input
                                type="checkbox"
                                checked={sendEmailOnSet}
                                onChange={(e) => setSendEmailOnSet(e.target.checked)}
                                disabled={!user.email}
                                className="h-4 w-4 rounded border-zinc-300"
                            />
                            {user.email ? `Email this password to ${user.email}` : 'No email on file — cannot email this password'}
                        </label>
                    </div>
                ) : (
                    <div className="mt-4">
                        <Text>
                            {user.email
                                ? `A random password will be generated and emailed to ${user.email}.`
                                : 'This user has no email on file, so a generated password cannot be delivered.'}
                        </Text>
                    </div>
                )}

                <DialogActions>
                    <Button plain onClick={closeResetDialog} disabled={isResetting}>
                        Cancel
                    </Button>
                    {resetMode === 'set' ? (
                        <Button onClick={handleSetPassword} disabled={isResetting}>
                            {isResetting ? 'Saving...' : 'Set Password'}
                        </Button>
                    ) : (
                        <Button onClick={handleGenerateAndEmail} disabled={isResetting || !user.email}>
                            {isResetting ? 'Sending...' : 'Generate & Email'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ViewAppUserIdPage;
